import { useState } from "react";
import type { AnnotationSelection } from "../types";
import type { LabelConfig } from "../api/s3";
import { addLabel, archiveLabel, restoreLabel } from "../api/s3";

interface Props {
  activeAnnotationId: string | null;
  selections: AnnotationSelection[];
  activeLabels: string[];
  archivedLabels: string[];
  onUpdate: (id: string, labels: string[], reasoning: string) => void;
  onClear: (id: string) => void;
  onLabelsChange: (config: LabelConfig) => void;
}

export default function LabelPanel({
  activeAnnotationId,
  selections,
  activeLabels,
  archivedLabels,
  onUpdate,
  onClear,
  onLabelsChange,
}: Props) {
  const active = selections.find((s) => s.id === activeAnnotationId) ?? null;
  const [showArchived, setShowArchived] = useState(false);
  const [newLabelValue, setNewLabelValue] = useState("");
  const [labelError, setLabelError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!active) {
    return (
      <div style={{ ...containerStyle, alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "#999", fontSize: 13 }}>Select text to label it</p>
      </div>
    );
  }

  const selectedText = getExactText(active);

  function toggleLabel(label: string) {
    const current = active!.labels ?? [];
    const next = current.includes(label)
      ? current.filter((l) => l !== label)
      : [...current, label];
    onUpdate(active!.id, next, active!.reasoning);
  }

  function setReasoning(reasoning: string) {
    onUpdate(active!.id, active!.labels, reasoning);
  }

  async function handleArchive(label: string) {
    if (busy) return;
    setBusy(true);
    try {
      const config = await archiveLabel(label);
      onLabelsChange(config);
    } catch {
      setLabelError("Archive failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRestore(label: string) {
    if (busy) return;
    setBusy(true);
    try {
      const config = await restoreLabel(label);
      onLabelsChange(config);
    } catch {
      setLabelError("Restore failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleAddLabel() {
    const name = newLabelValue.trim();
    if (!name || busy) return;
    // Capture active state before the await to avoid stale closure
    const capturedId = active!.id;
    const capturedLabels = active!.labels ?? [];
    const capturedReasoning = active!.reasoning;
    setBusy(true);
    setLabelError(null);
    try {
      const config = await addLabel(name);
      onLabelsChange(config);
      setNewLabelValue("");
      if (!capturedLabels.includes(name)) {
        onUpdate(capturedId, [...capturedLabels, name], capturedReasoning);
      }
    } catch (err: any) {
      const isArchived = err?.message?.includes("409");
      setLabelError(
        isArchived
          ? `"${name}" is archived — restore it from the Archived section.`
          : "Failed to add label. Try again."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={containerStyle}>
      <div style={sectionStyle}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Selection:</div>
        <div style={selectionTextStyle}>{selectedText}</div>
      </div>

      <div style={{ ...sectionStyle, flex: 1, overflowY: "auto", paddingBottom: 0 }}>
        {(activeLabels ?? []).map((label) => (
          <div key={label} style={labelRowStyle}>
            <label style={{ display: "flex", alignItems: "center", flex: 1, cursor: "pointer", overflow: "hidden" }}>
              <input
                type="checkbox"
                checked={(active.labels ?? []).includes(label)}
                onChange={() => toggleLabel(label)}
                style={{ marginRight: 8, flexShrink: 0 }}
              />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
            </label>
            <button onClick={() => handleArchive(label)} disabled={busy} style={iconBtnStyle} title="Archive label">🗄</button>
          </div>
        ))}

        {(archivedLabels ?? []).length > 0 && (
          <div>
            <button
              onClick={() => setShowArchived((v) => !v)}
              style={archivedToggleStyle}
            >
              {showArchived ? "▾" : "▸"} Archived ({(archivedLabels ?? []).length})
            </button>
            {showArchived && (archivedLabels ?? []).map((label) => (
              <div key={label} style={{ ...labelRowStyle, color: "#999", background: "#f5f5f5" }}>
                <span style={{ flex: 1, fontSize: 13 }}>{label}</span>
                <button onClick={() => handleRestore(label)} disabled={busy} style={iconBtnStyle} title="Restore label">↩</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: "10px 14px 4px", display: "flex", gap: 6 }}>
          <input
            value={newLabelValue}
            onChange={(e) => { setNewLabelValue(e.target.value); setLabelError(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddLabel(); }}
            placeholder="Add new label..."
            style={{ ...inlineInputStyle, flex: 1 }}
            disabled={busy}
          />
          <button onClick={handleAddLabel} disabled={busy} style={addBtnStyle} title="Add label">+</button>
        </div>
        {labelError && (
          <div style={{ padding: "0 14px 8px", fontSize: 12, color: "#b91c1c" }}>{labelError}</div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Reasoning</div>
        <textarea
          value={active.reasoning}
          onChange={(e) => setReasoning(e.target.value)}
          rows={4}
          style={textareaStyle}
          placeholder="Add reasoning..."
        />
      </div>

      <div style={{ padding: "10px 14px", borderTop: "1px solid #e0e0e0", flexShrink: 0 }}>
        <button onClick={() => onClear(active.id)} style={clearBtnStyle}>
          Clear
        </button>
      </div>
    </div>
  );
}

function getExactText(selection: AnnotationSelection): string {
  const targets = Array.isArray(selection.target)
    ? selection.target
    : [selection.target];
  for (const target of targets) {
    if (!target?.selector) continue;
    const selectors = Array.isArray(target.selector)
      ? target.selector
      : [target.selector];
    for (const sel of selectors) {
      if (sel?.type === "TextQuoteSelector") return sel.exact;
    }
  }
  return "";
}

const containerStyle: React.CSSProperties = {
  width: 280,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  background: "#fafafa",
  overflow: "hidden",
};

const sectionStyle: React.CSSProperties = {
  padding: "12px 14px",
  borderBottom: "1px solid #e0e0e0",
  flexShrink: 0,
};

const selectionTextStyle: React.CSSProperties = {
  background: "#fef9c3",
  padding: "6px 8px",
  borderRadius: 4,
  fontSize: 13,
  lineHeight: 1.5,
  wordBreak: "break-word",
};

const labelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "5px 14px",
  fontSize: 14,
  borderBottom: "1px solid #eee",
  gap: 4,
};

const iconBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "2px 4px",
  fontSize: 13,
  color: "#888",
  flexShrink: 0,
};

const inlineInputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: "3px 6px",
  border: "1px solid #ccc",
  borderRadius: 3,
  outline: "none",
};

const archivedToggleStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: 12,
  color: "#888",
  padding: "6px 14px",
  width: "100%",
  textAlign: "left",
};

const addBtnStyle: React.CSSProperties = {
  padding: "3px 10px",
  background: "#333",
  color: "#fff",
  border: "none",
  borderRadius: 3,
  cursor: "pointer",
  fontSize: 16,
  lineHeight: 1,
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  resize: "vertical",
  fontSize: 13,
  boxSizing: "border-box",
};

const clearBtnStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "#fff",
  border: "1px solid #ccc",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 13,
  color: "#555",
};
